import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { MODAL_EDIT_DOCUMENTS_GROUP } from '~/types/modal.types';
import { PilotId } from '~/types/PilotId';
import { ERROR, SUCCESS } from '~/types/toast.types';

type Props = {
  groupId: PilotId;
  name?: string;
  note?: string;
};

export const EditGroup = ({ groupId, name = '', note = '' }: Props) => {
  const [localName, setName] = useState(name);
  const [localNote, setNote] = useState(note);
  const dispatch = useDispatch();
  const intl = useIntl();

  const modal = useSelector(slices.documentGroup.selectors.selectModal);
  const { modalType } = modal || {};

  const [editDocumentGroup, result] =
    api.endpoints.editDocumentGroup.useMutation();
  const { isLoading } = result;

  const onClose = useCallback(() => {
    setName(name);
    setNote(note);
    dispatch(slices.documentGroup.actions.setModal());
  }, []);

  const isDirty = localName !== name || localNote !== note;

  return (
    <Modal
      isVisible={modalType === MODAL_EDIT_DOCUMENTS_GROUP}
      title={intl.formatMessage({
        id: 'EditGroup.title',
        defaultMessage: 'Edit Group',
        description: 'Modal title for editing group',
      })}
      onCancel={onClose}
      onHide={onClose}
      primaryAction={{
        text: intl.formatMessage({
          id: 'EditGroup.saveButton',
          defaultMessage: 'Save',
          description: 'Button text for saving group changes',
        }),
        onClick: () => {
          editDocumentGroup({
            groupId,
            data: { name: localName, note: localNote },
          })
            .unwrap()
            .then(() => {
              showToast(
                SUCCESS,
                intl.formatMessage({
                  id: 'EditGroup.editSuccess',
                  defaultMessage: 'Group has been edited successfully.',
                  description: 'Success message when group is edited',
                }),
              );
              onClose();
            })
            .catch((error) => {
              showToast(
                ERROR,
                coerceRtkqError(error)?.response?.data?.message ||
                  intl.formatMessage({
                    id: 'EditGroup.editError',
                    defaultMessage:
                      'An error occurred while updating a new document group.',
                    description: 'Error message when group editing fails',
                  }),
              );
            });
        },
        isLoading: isLoading,
        disabled: !localName?.trim() || isLoading || !isDirty,
      }}
    >
      <Layout direction="column">
        <Text mb={2} variant="body-medium">
          {intl.formatMessage({
            id: 'EditGroup.groupNameLabel',
            defaultMessage: 'GROUP NAME',
            description: 'Label for group name input field',
          })}
        </Text>
        <TextInput
          autoFocus
          name="name"
          placeholder={intl.formatMessage({
            id: 'EditGroup.groupNamePlaceholder',
            defaultMessage: 'Group Name',
            description: 'Placeholder text for group name input',
          })}
          value={localName}
          onChange={(value) => setName(value || '')}
        />
      </Layout>
      <Layout direction="column" mt={6}>
        <Text mb={2} variant="body-medium">
          {intl.formatMessage({
            id: 'EditGroup.noteLabel',
            defaultMessage: 'NOTE',
            description: 'Label for note input field',
          })}
        </Text>
        <TextArea
          name="note"
          placeholder={intl.formatMessage({
            id: 'EditGroup.notePlaceholder',
            defaultMessage: 'Note',
            description: 'Placeholder text for note input',
          })}
          value={localNote}
          onChange={(value) => setNote(value || '')}
        />
      </Layout>
    </Modal>
  );
};
